import React from "react";
import * as moment from "moment";
import { chain } from "underscore";
import { uniqBy } from "lodash";
import { findFieldValueForSection } from "underwriter_dashboard/shared";
import {
  AdditionalIncomeShape,
  AdditionalIncomeType,
  AppShape,
  DocShape,
  EmploymentShape,
  OwnedRealEstate,
  TaxIncomeSubsection,
  TaxIncomeSection,
  ReoOwnershipStatuses,
  ReoOwnershipStatusDisplayValues,
} from "@eave/shared/types/api";
import { linkToDocument, taxIncomeSectionFromSectionKey } from "./helpers";
import { ModifiedAggregationMap, TaxReturnIncomeType } from "./shared";
import TaxSectionTable from "./income_tables/expanded_row_partials/TaxSectionTable";
import AdditionalIncomeForBorrower from "../IncomeDocumentsReview/AdditionalIncomeForBorrower";
import TaxSubsectionTable from "./income_tables/expanded_row_partials/TaxSubsectionTable";
import { formatCurrency } from "@eave/shared/utils/currency";

export enum IncomeType {
  AdditionalIncome,
  TaxReturnIncome,
  RentalLeaseIncome, // OwnedRealEstate income
}

const {
  InstallmentSale,
  Interest,
  CapitalGains,
  Royalty,
  Farming,
  Trust,
  PositiveNetRentalRealEstate,
  // Associated with employment
  SoleProprietorship,
  Partnership,
  SCorporation,
  RegularCorporation,
} = TaxReturnIncomeType;

const incomesAssociatedWithEmployment = [
  SoleProprietorship,
  Partnership,
  SCorporation,
  RegularCorporation,
];

/**
 * Returns the content for the expanded row section depending on the type of incomeSection given
 * @param app AppShape The loan application
 * @param incomeType IncomeType The type of income, which determines what kind of worksheet is returned
 * @param incomeSection any The income section data
 * @param borrowerEntity number The borrower entity
 */
export const expandedRowContent = (
  app: AppShape,
  incomeType: IncomeType,
  incomeSection: AdditionalIncomeShape | TaxIncomeSection | OwnedRealEstate,
  borrowerEntity: number,
  modifiedAggregations: ModifiedAggregationMap,
  setModifiedAggregations: React.Dispatch<React.SetStateAction<ModifiedAggregationMap>>,
): JSX.Element => {
  if (incomeType === IncomeType.AdditionalIncome) {
    const { all_documents, additional_incomes, has_borrower2, income_summary } = app;
    const { operator_income_type } = incomeSection as AdditionalIncomeShape;

    // Grab the actual retirement worksheet, since some cases will need to display it
    const retirementSection = taxIncomeSectionFromSectionKey(
      income_summary.tax_summary.sections,
      "retirement",
    );

    const retirementIncomeTypes = [
      AdditionalIncomeType.SocialSecurity,
      AdditionalIncomeType.Pension,
      AdditionalIncomeType.IraDistribution,
      AdditionalIncomeType.IRA401K,
    ];

    const showRetirementWorksheet = retirementIncomeTypes.includes(
      operator_income_type as AdditionalIncomeType,
    );

    const requiresWorksheet = [Interest, CapitalGains, Royalty, Farming, Trust]
      .map(String)
      .includes(operator_income_type);

    if (!requiresWorksheet) {
      return <p>There are no associated worksheets for this income.</p>;
    }
    return (
      <>
        {showRetirementWorksheet && retirementSection ? (
          <>
            <TaxSectionTable
              app={app}
              section={retirementSection}
              qualification_year_minus_two={retirementSection.year_minus_two_total}
              qualification_year_minus_one={retirementSection.year_minus_one_total}
              modifiedAggregations={modifiedAggregations}
              setModifiedAggregations={setModifiedAggregations}
            />
          </>
        ) : (
          <AdditionalIncomeForBorrower
            key={`${borrowerEntity}-expanded-data`}
            additionalIncomes={additional_incomes.filter(i => i.borrower_entity === borrowerEntity)}
            header={`Borrower ${borrowerEntity} - Disclosed Additional Income`}
            incomeDocs={ownerID => {
              documentsForOwner(all_documents, "additional_income", ownerID);
            }}
            hasCoborrower={has_borrower2}
          />
        )}
      </>
    );
  }

  if (incomeType === IncomeType.TaxReturnIncome) {
    const { year_minus_one_total, year_minus_two_total } = incomeSection as TaxIncomeSection;
    return (
      <TaxSectionTable
        app={app}
        section={incomeSection}
        qualification_year_minus_one={year_minus_one_total}
        qualification_year_minus_two={year_minus_two_total}
        modifiedAggregations={modifiedAggregations}
        setModifiedAggregations={setModifiedAggregations}
      />
    );
  }

  if (incomeType === IncomeType.RentalLeaseIncome) {
    const { year_minus_one_total, year_minus_two_total } = incomeSection as TaxIncomeSection;

    // This rental income was ingested from a lease agreement, so remove the Schedule E-related subsection
    const incomeSectionWithLeaseWorksheet = {
      ...incomeSection,
      subsections: (incomeSection as TaxIncomeSection).subsections.filter(
        subsection => subsection.aggregation?.key === "rental_real_estate_lease",
      ),
    };

    return (
      <TaxSectionTable
        app={app}
        section={incomeSectionWithLeaseWorksheet}
        qualification_year_minus_one={year_minus_one_total}
        qualification_year_minus_two={year_minus_two_total}
        modifiedAggregations={modifiedAggregations}
        setModifiedAggregations={setModifiedAggregations}
      />
    );
  }

  return <p>There are no associated worksheets for this income.</p>;
};

/**
 * Returns the content for the document_name column
 * @param documents DocShape[] The list of documents to render as links
 */
const documentLink = (documents: DocShape[]) => {
  if (documents.length === 0) {
    return "-";
  }
  return (
    <>
      {documents.map((doc): string | JSX.Element => {
        if (!doc || !doc.sections || !doc.sections.length) {
          return "-";
        }

        return linkToDocument(doc, doc.display_text);
      })}
    </>
  );
};

/**  Given a list of documents, will return a subset containing documents belonging to a specific "owner" entity
 * @param {DocShape[]} docs - Any set of documents
 * @param {string} ownerType - The type of owner
 * @param {number} ownerID - The id of the owner
 */
export const documentsForOwner = (docs: DocShape[], ownerType: string, ownerID: number) => {
  return docs.filter(
    document => document.owner_entity_type === ownerType && document.owner_entity_id === ownerID,
  );
};

/**  Given a list of documents, will return a subset containing used, "income_other" docs with a positive monthly_amount
 * @param {DocShape[]} docs - Any set of documents
 * @returns {DocShape[]} - Subset of docs that are used, of type "income_other", with a positive monthly_amount
 */
export const validIncomeOtherDocs = (docs: DocShape[]) => {
  return docs.filter(doc => {
    const isUsed = doc.used;
    const isIncomeOtherDoc = doc.type === "income_other";

    const documentSectionWithMonthlyTotal = doc.sections
      ?.find(s => s.section_key === "main")
      ?.fields?.find(f => f.field_key === "monthly_amount");
    if (!documentSectionWithMonthlyTotal) {
      return false;
    }
    const hasPositiveMonthlyTotal = parseFloat(documentSectionWithMonthlyTotal.field_value) > 0;
    return isUsed && isIncomeOtherDoc && hasPositiveMonthlyTotal;
  });
};

type IncomeSectionTableHeaders = {
  id: string;
  heading: string;
  monthly_total: string;
};

/**
 * The shape of a table row data object - as defined by the table's header data
 */
export type IncomeSectionFields = {
  associated_document_ids: number[];
  allAssociatedDocuments?: DocShape[];
  previous_associated_document_ids?: number[];
  redirect_to_documents_hurdle_for_edits: boolean;
  document_name: any;
  expanded_data: JSX.Element;
  excludeFromTotal?: boolean;
};

export type IncomeSectionTableRow = IncomeSectionTableHeaders & IncomeSectionFields;

/**
 * The shape of an Additional Incomes table row data object
 */
export type AdditionalIncomeTableRow = Omit<AdditionalIncomeShape, "id"> & IncomeSectionTableRow;
export const additionalIncomeToTableRow = (
  app: AppShape,
  incomeSection: AdditionalIncomeShape,
  borrowerEntity: number,
  modifiedAggregations,
  setModifiedAggregations,
): AdditionalIncomeTableRow => {
  const { all_documents } = app;

  const { id: incomeSectionId, description, final_monthly_income } = incomeSection;

  // A single document
  const docs = documentsForOwner(all_documents, "additional_income", incomeSectionId);

  return {
    ...incomeSection,
    id: `${incomeSectionId}`,
    associated_document_ids: docs.map(({ id }) => id),
    redirect_to_documents_hurdle_for_edits: false,
    heading: description,
    monthly_total: final_monthly_income,
    document_name: documentLink(docs),
    expanded_data: expandedRowContent(
      app,
      IncomeType.AdditionalIncome,
      incomeSection,
      borrowerEntity,
      modifiedAggregations,
      setModifiedAggregations,
    ),
    excludeFromTotal: false,
  };
};

/**
 * The shape of an Tax Return Incomes table row data object
 */
export type TaxIncomeSectionTableRow = TaxIncomeSection & IncomeSectionTableRow;
export const taxReturnIncomeToTableRow = (
  app: AppShape,
  incomeSection: TaxIncomeSection,
  borrowerEntity: number,
  modifiedAggregations,
  setModifiedAggregations,
): TaxIncomeSectionTableRow => {
  const { documents } = app;
  const { section_key, monthly_total } = incomeSection;

  const docs = documents.filter(
    doc =>
      doc.owner_entity_type === "additional_income" && doc.type.startsWith("income_tax_return"),
  );

  return {
    ...incomeSection,
    id: `$${app.id}-${section_key}`,
    associated_document_ids: docs.map(({ id }) => id),
    allAssociatedDocuments: docs,
    redirect_to_documents_hurdle_for_edits: true,
    monthly_total,
    document_name: documentLink(docs),
    expanded_data: expandedRowContent(
      app,
      IncomeType.TaxReturnIncome,
      incomeSection,
      borrowerEntity,
      modifiedAggregations,
      setModifiedAggregations,
    ),
    excludeFromTotal: false,
  };
};

/**
 * The shape of an Owned Real Estate: Rental Incomes table row data object
 */
export type OwnedRealEstateMeta = Omit<OwnedRealEstate, "id"> & {
  allAssociatedDocuments: DocShape[];
};
export type OwnedRealEstateTableRow = OwnedRealEstateMeta & IncomeSectionTableRow;
export const rentalIncomeToTableRow = (
  app: AppShape,
  reo: OwnedRealEstate,
  borrowerEntity: number,
  modifiedAggregations,
  setModifiedAggregations,
  cumulativeREOIncomeIsPositive: boolean,
): OwnedRealEstateTableRow => {
  const {
    income_summary: {
      tax_summary: { sections },
    },
    all_documents: allDocuments,
  } = app;
  const {
    id: reoId,
    documents: reoDocuments,
    schedule_e_property_labels: scheduleEPropertyLabels,
    tax_income_subsections: taxIncomeSubsections, // populated regardless of ingestion source
  } = reo;

  // The document is either ingested from a tax return or an Investment Property Lease agreement
  const isIngestedFromTaxReturn = scheduleEPropertyLabels.length > 0;
  const scheduleEIds = scheduleEPropertyLabels.map(d => d.document_id);

  // Rental incomes may be associated to multiple docs (lease agreement as well as tax return), so get a unique list of all associated documents
  const allAssociatedDocuments = uniqBy(
    [...taxReturns(allDocuments).filter(doc => scheduleEIds.includes(doc.id)), ...reoDocuments],
    "id",
  );

  // We want to show the rental income worksheet, so lets grab the rental_real_estate section
  const rentalIncomeSection = taxIncomeSectionFromSectionKey(
    sections,
    TaxReturnIncomeType.PositiveNetRentalRealEstate,
  );

  // An REO may be linked to both a lease agreement and a tax return,
  //  but only one should be included in ratios
  const associatedSubsection = taxIncomeSubsections.find(
    subsection => subsection.aggregation.include_in_ratios,
  );

  const isARentalProperty = resolvedOwnershipStatus(reo) === ReoOwnershipStatuses.RentedOut;

  // REOs totals are only included if the cumulative REO income is positive
  const excludeFromTotal = !isARentalProperty || !cumulativeREOIncomeIsPositive;

  return {
    ...reo,
    id: `$${app.id}-${reoId}`,
    heading: `Rental - ${reo.operator_address_line1 || reo.address_line1}`,
    associated_document_ids: [],
    previous_associated_document_ids: [],
    allAssociatedDocuments,
    redirect_to_documents_hurdle_for_edits: true,
    monthly_total: `${associatedSubsection?.monthly_total || 0}`,
    document_name: documentLink(allAssociatedDocuments),
    expanded_data: expandedRowContent(
      app,
      isIngestedFromTaxReturn ? IncomeType.TaxReturnIncome : IncomeType.RentalLeaseIncome,
      rentalIncomeSection,
      borrowerEntity,
      modifiedAggregations,
      setModifiedAggregations,
    ),
    excludeFromTotal,
  };
};

/**
 *  Returns tax return incomes that are not-associated with an employment, and which require a worksheet to be displayed
 * @param taxSummarySections
 * @returns
 */
export const validTaxReturnIncomes = (taxSummarySections: TaxIncomeSection[]): TaxIncomeSection[] =>
  taxSummarySections.filter(section => {
    const isNotAssociatedWithEmployment = !incomesAssociatedWithEmployment
      .map(String)
      .includes(section.section_key);

    const shouldShowWorksheet = [InstallmentSale, Interest, CapitalGains, Royalty, Farming, Trust]
      .map(String)
      .includes(section.section_key);

    return shouldShowWorksheet && isNotAssociatedWithEmployment;
  });

export const getOtherIncomesForBorrower = (
  app: AppShape,
  borrowerEntity: number,
  modifiedAggregation,
  setModifiedAggregations,
) => {
  const { additional_incomes: additionalIncomeSections, income_summary } = app;
  const { sections: taxSummarySections } = income_summary.tax_summary;

  const { NoteReceivable } = AdditionalIncomeType;
  const installmentSaleSection = taxSummarySections.find(
    s => s.section_key === `${InstallmentSale}`,
  );

  // "Tax Return Income" refers to non-salaried incomes that are ingested via tax returns
  const taxReturnIncomes = validTaxReturnIncomes(taxSummarySections).map(section => {
    // For NoteReceivable Income, we should show the Installment Sale worksheet
    if (section.section_key === `${NoteReceivable}`) {
      return taxReturnIncomeToTableRow(
        app,
        installmentSaleSection,
        borrowerEntity,
        modifiedAggregation,
        setModifiedAggregations,
      );
    }
    return taxReturnIncomeToTableRow(
      app,
      section,
      borrowerEntity,
      modifiedAggregation,
      setModifiedAggregations,
    );
  });

  // "Additional Income" refers to non-salaried incomes that are NOT ingested via tax returns
  const additionalIncomes = additionalIncomeSections
    .filter(section => section.borrower_entity === borrowerEntity)
    .map(section =>
      additionalIncomeToTableRow(
        app,
        section,
        borrowerEntity,
        modifiedAggregation,
        setModifiedAggregations,
      ),
    );

  const cumulativeREOIncome = app.owned_real_estate
    .filter(isInvestmentProperty)
    .reduce((acc, curr) => {
      const associatedSubsection = curr.tax_income_subsections.find(
        subsection => subsection.aggregation.include_in_ratios,
      );

      if (associatedSubsection?.monthly_total) {
        return acc + Number(associatedSubsection.monthly_total);
      }
      return acc;
    }, 0);

  const cumulativeREOIncomeIsPositive = cumulativeREOIncome > 0;

  const rentalIncomes = app.owned_real_estate
    .filter(isInvestmentProperty)
    .filter((reo: OwnedRealEstate) => {
      // Everything that's not specifically for b2 should display in B1's table
      if (borrowerEntity === 1) {
        return reo.borrower_entity !== 2;
      }
      if (borrowerEntity === 2) {
        return reo.borrower_entity === 2;
      }
    })
    .map(reo =>
      rentalIncomeToTableRow(
        app,
        reo,
        borrowerEntity,
        modifiedAggregation,
        setModifiedAggregations,
        cumulativeREOIncomeIsPositive,
      ),
    );

  // Return a list of all non-zero tax return incomes and all additional incomes
  const otherIncomes = [
    ...taxReturnIncomes.filter(i => parseFloat(i.monthly_total) > 0),
    ...additionalIncomes,
  ];

  return [...otherIncomes, ...rentalIncomes.filter(i => parseFloat(i.monthly_total) !== 0)];
};

export const rentalProperties = (ownedRealEstate: OwnedRealEstate[]) => {
  return ownedRealEstate.filter(
    (property: any) =>
      property.operator_ownership_status === "RentedOut" ||
      property.ownership_status === "RentedOut",
  );
};

export const propertyLabelAndAddressFromScheduleE = (taxReturnDocument: any) => {
  // A, B, and C are the labels of properties on schedule E
  const propertyLabels = ["A", "B", "C"];
  return propertyLabels.reduce(
    (acc, label) => ({
      ...acc,
      [label]: findFieldValueForSection(
        taxReturnDocument,
        `property_${label.toLowerCase()}`,
        "schedule_e",
      ),
    }),
    {},
  );
};

export const propertyLabelAndAddressOptions = taxReturnDocument => {
  const propertyLabelAndAddress = propertyLabelAndAddressFromScheduleE(taxReturnDocument);

  const options = Object.keys(propertyLabelAndAddress).map(propertyLabel => {
    const address = propertyLabelAndAddress[propertyLabel];
    const label = address ? `Property ${propertyLabel} - ${address}` : `Property ${propertyLabel}`;

    return {
      label,
      value: propertyLabel,
    };
  });

  options.push({
    label: "Does not appear on this tax return",
    value: "NA", // null is taken by react-select as no option selected!
    // here, operator has chosen that it doesn't appear on the tax return.
    // specify that choice, rather than leaving as unselected, so that operator
    // coming back here, say for a 2nd time, knows that matching the REO to the schedule E
    // has already been addressed (and in this case, there is no match).
  });

  return options;
};

export const propertyLabelAndAddressOptionSelected = (
  ownedRealEstateIDsToScheduleEPropertyLabels,
  ownedRealEstateID,
  taxReturnDocument,
) => {
  // get the options again (below) -- can't only use state, since state stores
  // only the property label (not the address hint, which is part of what is displayed
  // as `property label - address` for human matching)
  const selected = propertyLabelAndAddressOptions(taxReturnDocument).filter(
    option =>
      option.value ===
      ownedRealEstateIDsToScheduleEPropertyLabels[ownedRealEstateID][taxReturnDocument.id],
  );

  return selected.length > 0 ? selected[0] : null;
};

/**  Given a list of documents, will return a subset containing income tax returns
 * @param {DocShape[]} allDocuments - Usually from app.all_documents
 */
export const taxReturns = (allDocuments: DocShape[]) => {
  return allDocuments.filter(document => document.type.startsWith("income_tax_return_"));
};

export const filterAndSortSalariedCurrentEmployments = (app, employments): EmploymentShape[] => {
  const isPrimary = employment =>
    (employment.borrower_entity === 1 &&
      employment.id === app.borrower1_primary_income_id &&
      (app.borrower1_primary_income_source === "salaried" ||
        app.borrower1_primary_income_source === "selfEmployed")) ||
    (employment.borrower_entity === 2 &&
      employment.id === app.borrower2_primary_income_id &&
      (app.borrower2_primary_income_source === "salaried" ||
        app.borrower2_primary_income_source === "selfEmployed"));

  return chain(employments)
    .sortBy((employment: any) => -1 * Number(moment(employment.start_date).format("x")))
    .sortBy((employment: any) => !isPrimary(employment))
    .sortBy((employment: any) => employment.borrower_entity)
    .filter(
      (employment: any) => employment.end_date === null && employment.income_type === "salaried",
    )
    .value();
};

export const isInvestmentProperty = (reo: OwnedRealEstate): boolean => {
  // ownershipTypesThatMapToInvestment is based on the mapping below:
  // https://github.com/homelight/eave-go/blob/main/fnma/mismo34/enumeration_mapping.go#L93
  const ownershipTypesThatMapToInvestment = [
    ReoOwnershipStatuses.RentedOut,
    ReoOwnershipStatuses.OtherInvestment,
  ];

  return ownershipTypesThatMapToInvestment.includes(resolvedOwnershipStatus(reo));
};

// `operator_ownership_status` takes precedence
export const resolvedOwnershipStatus = (reo: OwnedRealEstate) =>
  reo.operator_ownership_status || reo.ownership_status;
